import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Box,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import SearchIcon from "@material-ui/icons/Search";
import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPatch } from "../getConfig";

const useStyles = makeStyles(() => ({
  main: { padding: 24, backgroundColor: "#f8f9fa", minHeight: "100vh" },
  title: { fontSize: 24, fontWeight: 600, marginBottom: 4 },
  subtitle: { fontSize: 14, color: "#666", marginBottom: 24 },
  paper: { padding: 20, marginBottom: 16, borderRadius: 8 },
  searchRow: { display: "flex", alignItems: "center", gap: 12 },
  statBox: { textAlign: "center" as const, padding: 12, borderRadius: 8 },
  statValue: { fontSize: 22, fontWeight: 700 },
  statLabel: { fontSize: 11, color: "#666", marginTop: 2 },
  bucket: {
    flex: 1,
    textAlign: "center" as const,
    padding: "10px 4px",
    borderRadius: 6,
    backgroundColor: "#f1f3f5",
    margin: "0 3px",
  },
  bucketValue: { fontSize: 16, fontWeight: 700 },
  bucketLabel: { fontSize: 10, color: "#666" },
  row: { cursor: "pointer" },
  actionRow: { display: "flex", flexWrap: "wrap" as const, gap: 8 },
  warn: { fontSize: 12, color: "#b26a00", marginTop: 8 },
  sectionTitle: { fontWeight: 600, fontSize: 15, marginBottom: 4 },
  lede: { fontSize: 12, color: "#666", marginBottom: 14, maxWidth: 780 },
  alert: {
    padding: "12px 16px",
    borderRadius: 8,
    backgroundColor: "#fff4e5",
    border: "1px solid #ffcc80",
    marginBottom: 16,
  },
  alertTitle: { fontWeight: 700, fontSize: 13, color: "#e65100", marginBottom: 6 },
  alertItem: { fontSize: 13, color: "#8a5a00" },
  num: { fontVariantNumeric: "tabular-nums" as const, whiteSpace: "nowrap" as const },
  rateCell: { color: "#666", fontSize: 12 },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap" as const,
  },
  headline: { fontSize: 13, color: "#333", marginTop: 10, lineHeight: 1.6 },
  tabBar: {
    backgroundColor: "#fff",
    borderRadius: 8,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
  /**
   * Two per row on a normal screen, one on a narrow one. auto-fit rather than
   * a fixed count so it collapses without a breakpoint, matching the grid the
   * Anywhere dashboard already uses.
   */
  pairGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(430px, 1fr))",
    gap: 16,
    alignItems: "start",
    marginBottom: 16,
  },
  /** Paper inside pairGrid: the grid owns the spacing, not the card. */
  paperFlush: { padding: 20, borderRadius: 8, marginBottom: 0, height: "100%" },
  verdict: {
    padding: "14px 18px",
    borderRadius: 8,
    backgroundColor: "#fff",
    borderLeft: "4px solid #1e88e5",
    marginBottom: 16,
    fontSize: 14,
    lineHeight: 1.6,
    color: "#222",
  },
  verdictLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase" as const,
    color: "#1e88e5",
    marginBottom: 4,
  },
}));

const ENDPOINT = `${API}/admin/new-user-boost`;

/** Matches the backend switch. Both flip together. */
const SUPPORT_ACTIONS_ENABLED = true;

/** Ten a page keeps the per-page shop count cheap and the table readable. */
const PAGE_SIZE = 10;

/**
 * Label, which extra field the action needs, and whether it is privileged.
 *
 * privileged mirrors PRIVILEGED_ACTIONS in the backend, which holds these to
 * the admin and ops roles. Marked here only so the operator is warned before
 * they type a reason: this screen has no role of its own to check, because
 * login stores a token and never the permissions, so a wrong tier is refused
 * by the API rather than hidden by the dropdown.
 *
 * Refreshing the cache is the only action that changes nothing a customer can
 * earn. Everything else moves earning in one direction or the other, including
 * the two that take it away.
 */
const ACTIONS: {
  key: string;
  label: string;
  field?: "days" | "shops" | "brandId";
  privileged?: boolean;
}[] = [
  { key: "EXTEND_LAPSE", label: "Extend lapse", field: "days", privileged: true },
  { key: "OPEN_LAPSE_OFFER", label: "Open lapse offer", privileged: true },
  { key: "FORCE_CONCLUDE", label: "Force conclude", privileged: true },
  { key: "REOPEN_JOURNEY", label: "Reopen journey", privileged: true },
  {
    key: "EXTEND_BOOST_WINDOW",
    label: "Extend boost window",
    field: "days",
    privileged: true,
  },
  { key: "REFRESH_BOOST_CACHE", label: "Refresh boost cache" },
  {
    key: "RELEASE_BRAND",
    label: "Release a brand",
    field: "brandId",
    privileged: true,
  },
  {
    key: "CREDIT_SHOP",
    label: "Credit a shop",
    field: "shops",
    privileged: true,
  },
];

const stateColour = (state: string): string => {
  if (state === "concluded") return "#6c757d";
  if (state === "lapsed") return "#e8a838";
  return "#2e7d32";
};

/** The refusal reason on an audit row, when the attempt was refused. */
const refusalOf = (row: any): string | null => {
  try {
    const meta =
      typeof row?.metadata === "string"
        ? JSON.parse(row.metadata)
        : row?.metadata;
    return meta?.refused || null;
  } catch {
    return null;
  }
};

const money = (n: any) => `£${Number(n || 0).toFixed(2)}`;
const when = (v: any) => (v ? new Date(v).toLocaleString("en-GB") : "-");

/** A shop is a payment, and three kinds of payment count. */
const SHOP_SOURCES: Record<string, string> = {
  gift_card: "Gift card",
  top_up: "Top-up",
  anywhere_card: "Anywhere card"
};

/**
 * Every rung of this customer's ladder, not only the ones already credited.
 *
 * The panel used to render `rewards`, which holds a row per rung actually
 * paid. A customer who had lapsed but not yet shopped again therefore saw
 * "no rungs recorded yet" next to a "still to earn" figure, and support could
 * not tell them what the next shop was worth. `ladder` carries the priced
 * rungs and the backend has always sent it.
 */
const ladderRows = (detail: any) => {
  const rungs: Record<string, any> = detail?.ladder || {};
  const paid = new Map<string, any>(
    (detail?.rewards || []).map((r: any) => [String(r.shop_number), r])
  );

  // Union, not the ladder alone: a rung credited under an earlier lapse point
  // is money this customer was paid, and this ladder does not contain it.
  // Listing only the ladder would drop it off the screen.
  const shopNumbers = Array.from(
    new Set([...Object.keys(rungs), ...Array.from(paid.keys())])
  );

  return shopNumbers
    .map((shopNumber) => {
      const credited = paid.get(shopNumber);
      return {
        shopNumber: Number(shopNumber),
        // What was paid, when it was paid. The config can move after a rung is
        // credited, and the amount on the payment is the true one.
        amount: credited ? credited.amount : rungs[shopNumber],
        id: credited?.id,
        paid: Boolean(credited?.credited_at && !credited?.reversed_at),
        state: credited?.reversed_at
          ? "reversed"
          : credited?.credited_at
          ? "paid"
          : "not earned yet"
      };
    })
    .sort((a, b) => a.shopNumber - b.shopNumber);
};

/** A rate the backend could not compute reads as "n/a", never as 0% or NaN%. */
const pct = (v: any) =>
  v === null || v === undefined ? "n/a" : `${Math.round(Number(v) * 100)}%`;

/**
 * The backend keys each row on a local date, so parse it as one. Reading it as
 * UTC and rendering it locally moved every row a day back under BST.
 */
const day = (v: any) => {
  const parts = String(v ?? "").split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return "-";
  return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const STATE_FILTERS = [
  { value: "", label: "Any state" },
  { value: "active", label: "Active" },
  { value: "awaiting_selection", label: "Awaiting brand pick" },
  { value: "lapsed", label: "Lapsed" },
  { value: "offer_open", label: "Offer open" },
  { value: "concluded", label: "Concluded" },
];

/**
 * Only counters that should stay at zero. A number that is non-zero in normal
 * running lights the banner permanently, which is how the one that means money
 * is stuck stops being read.
 */
const HEALTH_LABELS: Record<string, string> = {
  rungsUncreditedOver24h: "Rungs awarded but uncredited over 24h",
  lapsedWithoutLapsePoint: "Lapsed with no lapse point recorded",
  confirmedWithoutBrands: "Confirmed a pick but holds no brands",
  rungsWithoutJourney: "Credited rung not linked to a journey",
};

/**
 * The performance read, in sections.
 *
 * Split out of the dashboard rather than nested in it: React counts the
 * complexity of every JSX guard and ternary against the enclosing function, so
 * four tables inline pushed the parent well past any sane threshold. Each
 * section takes only the slice of the payload it draws.
 */
interface AnalysisProps {
  analysis: any;
  trendDays: number;
  onTrendDays: (days: number) => void;
  exporting: boolean;
  onExport: () => void;
}

/** Only counters that should be zero, so a lit banner always means something. */
const HealthAlerts = ({ health }: { health: any }) => {
  const classes = useStyles();
  const alerts = Object.entries(health || {})
    .map(([key, value]) => ({
      key,
      label: HEALTH_LABELS[key] || key,
      value: Number(value || 0),
    }))
    .filter((a) => a.value > 0);

  if (!alerts.length) return null;

  return (
    <div className={classes.alert}>
      <div className={classes.alertTitle}>
        {alerts.length === 1
          ? "1 thing needs attention"
          : `${alerts.length} things need attention`}
      </div>
      {alerts.map((a) => (
        <div key={a.key} className={classes.alertItem}>
          {a.label}: <b>{a.value}</b>
        </div>
      ))}
    </div>
  );
};

const FunnelSection = ({
  funnel,
  targetShops,
}: {
  funnel: any;
  targetShops: number;
}) => {
  const classes = useStyles();
  if (!funnel) return null;

  const stages = [
    { label: "Enrolled", value: funnel.enrolled, rate: "" },
    {
      label: "Picked brands",
      value: funnel.picked,
      rate: `${pct(funnel.rates.enrolledToPicked)} of enrolled`,
    },
    {
      label: "Shopped a pick",
      value: funnel.shoppedAPick,
      rate: `${pct(funnel.rates.pickedToShopped)} of those who picked`,
    },
    {
      label: `Reached ${targetShops} shops`,
      value: funnel.reachedTarget,
      rate: `${pct(funnel.rates.enrolledToTarget)} of enrolled`,
    },
    {
      label: "Went quiet and lapsed",
      value: funnel.everLapsed,
      rate: `${pct(funnel.rates.enrolledToLapsed)} of enrolled`,
    },
  ];

  return (
    <Paper className={classes.paperFlush}>
      <div className={classes.sectionTitle}>Is it working</div>
      <div className={classes.lede}>
        How far customers get once they enrol. "Shopped a pick" is the boost
        doing its job: they chose a brand and then bought it. Choosing four
        brands and buying none is not engagement, so the pick rate alone would
        flatter us.
      </div>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Stage</TableCell>
            <TableCell align="right">Customers</TableCell>
            <TableCell align="right">Rate</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stages.map((s) => (
            <TableRow key={s.label}>
              <TableCell>{s.label}</TableCell>
              <TableCell align="right" className={classes.num}>
                {s.value}
              </TableCell>
              <TableCell
                align="right"
                className={`${classes.num} ${classes.rateCell}`}
              >
                {s.rate}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

/**
 * Says plainly when the sample is too small to read, so a full recovery rate
 * built on one customer cannot be mistaken for evidence.
 */
const verdictFor = (recovery: any, spend: any): string => {
  if (!recovery) return "";
  if (!recovery.lapsed) {
    return "No customer has lapsed yet, so the comeback offer is untested. Nothing here can be read as evidence either way until it has run.";
  }
  if (recovery.lapsed < 20) {
    return `Only ${recovery.lapsed} customer${
      recovery.lapsed === 1 ? " has" : "s have"
    } lapsed so far, which is too few to draw a conclusion from. The rates below are real but will move a lot.`;
  }
  return `${recovery.recovered} of ${recovery.lapsed} lapsed customers came back, buying ${recovery.shopsGained} extra shops for ${money(
    spend?.paid
  )}.`;
};

const RecoverySection = ({
  recovery,
  spend,
}: {
  recovery: any;
  spend: any;
}) => {
  const classes = useStyles();
  if (!recovery) return null;

  const rows = [
    {
      label: "Lapsed and were offered the ladder",
      value: recovery.lapsed,
      note: `${recovery.offerOpen} still open`,
    },
    {
      label: "Came back and shopped again",
      value: recovery.recovered,
      note: `${pct(recovery.recoveryRate)} of those offered`,
    },
    {
      label: "Extra shops bought",
      value: recovery.shopsGained,
      note: recovery.shopsPerRecovered
        ? `${recovery.shopsPerRecovered} per returning customer`
        : "",
    },
    {
      label: "Paid out so far",
      value: money(spend?.paid),
      note: `${spend?.rungsPaid || 0} rungs credited`,
    },
    {
      label: "Committed if every open offer completes",
      value: money(spend?.committed),
      note: "a ceiling, not a forecast",
    },
    {
      label: "Cost per extra shop",
      value:
        spend?.costPerIncrementalShop === null ||
        spend?.costPerIncrementalShop === undefined
          ? "n/a"
          : money(spend.costPerIncrementalShop),
      note: "paid out, divided by shops gained",
    },
  ];

  return (
    <Paper className={classes.paperFlush}>
      <div className={classes.sectionTitle}>
        Does the comeback offer bring anyone back
      </div>
      <div className={classes.lede}>
        The whole thesis of the ladder. Recovery is measured against the shop
        count frozen when the offer opened, so a shop made before the lapse
        cannot be counted as a comeback.
      </div>
      <Table size="small">
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.label}>
              <TableCell>{r.label}</TableCell>
              <TableCell align="right" className={classes.num}>
                <b>{r.value}</b>
              </TableCell>
              <TableCell className={classes.rateCell}>{r.note}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className={classes.headline}>{verdictFor(recovery, spend)}</div>
    </Paper>
  );
};

const CampaignExportSection = ({
  exporting,
  onExport,
}: {
  exporting: boolean;
  onExport: () => void;
}) => {
  const classes = useStyles();
  return (
    <Paper className={classes.paperFlush}>
      <div className={classes.sectionTitle}>Bring quiet customers back</div>
      <div className={classes.lede}>
        Customers who have gone quiet long enough to qualify for the ladder but
        cannot be shown it, because the state is worked out when they open the
        app and these are the people not opening it. Downloads their email and
        push id so marketing can message them. Only customers inside the cohort
        are listed: anyone outside it would answer the push and then be
        refused, which is worse than never being messaged.
      </div>
      <Button
        variant="contained"
        color="primary"
        disabled={exporting}
        onClick={onExport}
      >
        {exporting ? "Building..." : "Export campaign list (CSV)"}
      </Button>
      <div className={classes.warn}>
        Nobody is put into the lapsed state by this. Their seven day offer opens
        when they arrive, so it cannot expire before they have seen it.
      </div>
    </Paper>
  );
};

const DailyTrendSection = ({
  daily,
  days,
  onDays,
}: {
  daily: any[];
  days: number;
  onDays: (days: number) => void;
}) => {
  const classes = useStyles();
  return (
    <Paper className={classes.paper}>
      <div className={classes.sectionTitle}>Day by day</div>
      <div className={classes.filterRow} style={{ marginBottom: 12 }}>
        <TextField
          select
          size="small"
          variant="outlined"
          label="Window"
          value={days}
          onChange={(e) => onDays(Number(e.target.value))}
          style={{ minWidth: 140 }}
        >
          {[7, 14, 30, 60, 90].map((d) => (
            <MenuItem key={d} value={d}>
              Last {d} days
            </MenuItem>
          ))}
        </TextField>
      </div>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Day</TableCell>
            <TableCell align="right">Registered</TableCell>
            <TableCell align="right">Joined</TableCell>
            <TableCell align="right">Picked brands</TableCell>
            <TableCell align="right">Lapsed</TableCell>
            <TableCell align="right">Concluded</TableCell>
            <TableCell align="right">Rungs paid</TableCell>
            <TableCell align="right">Paid</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(daily || []).map((d: any) => (
            <TableRow key={d.day}>
              <TableCell className={classes.num}>{day(d.day)}</TableCell>
              <TableCell align="right" className={classes.num}>
                {d.registered || "-"}
              </TableCell>
              <TableCell align="right" className={classes.num}>
                {d.enrolled || "-"}
              </TableCell>
              <TableCell align="right" className={classes.num}>
                {d.picked || "-"}
              </TableCell>
              <TableCell
                align="right"
                className={classes.num}
                style={{ color: d.lapsed ? "#c62828" : undefined }}
              >
                {d.lapsed || "-"}
              </TableCell>
              <TableCell align="right" className={classes.num}>
                {d.concluded || "-"}
              </TableCell>
              <TableCell align="right" className={classes.num}>
                {d.rungs || "-"}
              </TableCell>
              <TableCell align="right" className={classes.num}>
                {d.paid ? money(d.paid) : "-"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className={classes.warn}>
        Registered counts everyone who signed up inside the cohort cutoff,
        whether or not they went on to enrol. Joined counts the journeys
        created.
      </div>
    </Paper>
  );
};

const BrandSection = ({ brands }: { brands: any[] }) => {
  const classes = useStyles();
  if (!brands || !brands.length) return null;

  return (
    <Paper className={classes.paperFlush}>
      <div className={classes.sectionTitle}>Which brands get picked</div>
      <div className={classes.lede}>
        Conversion is the share of picks that were then shopped. A brand picked
        often and shopped rarely is decoration on the grid.
      </div>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Brand</TableCell>
            <TableCell align="right">Picks</TableCell>
            <TableCell align="right">Shopped</TableCell>
            <TableCell align="right">Conversion</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {brands.map((b: any) => (
            <TableRow key={b.brandId}>
              <TableCell>{b.brandName || `#${b.brandId}`}</TableCell>
              <TableCell align="right" className={classes.num}>
                {b.picks}
              </TableCell>
              <TableCell align="right" className={classes.num}>
                {b.shopped}
              </TableCell>
              <TableCell align="right" className={classes.num}>
                {pct(b.conversion)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

/**
 * Paired two to a row rather than stacked ten deep.
 *
 * The funnel and the recovery answer the same question from two sides, so they
 * read together. The trend keeps the full width because it carries eight
 * columns and halving it squeezed them; the brand table and the export are
 * both narrow and pair cleanly beneath it. Health alerts and the verdict are
 * lifted out to the page, above the tabs, because they apply to either tab.
 */
const PerformanceView = ({
  analysis,
  trendDays,
  onTrendDays,
  exporting,
  onExport,
}: AnalysisProps) => {
  const classes = useStyles();
  if (!analysis) return null;

  return (
    <>
      <div className={classes.pairGrid}>
        <FunnelSection
          funnel={analysis.funnel}
          targetShops={analysis.config?.targetShops}
        />
        <RecoverySection recovery={analysis.recovery} spend={analysis.spend} />
      </div>
      <DailyTrendSection
        daily={analysis.daily}
        days={trendDays}
        onDays={onTrendDays}
      />
      <div className={classes.pairGrid}>
        <BrandSection brands={analysis.brands} />
        <CampaignExportSection exporting={exporting} onExport={onExport} />
      </div>
    </>
  );
};

const NewUserBoostDashboard = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [summary, setSummary] = useState<any>(null);
  const [analysis, setAnalysis] = useState<any>(null);
  const [trendDays, setTrendDays] = useState(14);
  // Leadership reads the first tab, support works in the second. One screen
  // held both, so each audience scrolled through the other's tooling.
  const [tab, setTab] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [enrolledFrom, setEnrolledFrom] = useState("");
  const [enrolledTo, setEnrolledTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<any>(null);
  const rungs = useMemo(() => ladderRows(detail), [detail]);

  const [action, setAction] = useState("");
  const [reason, setReason] = useState("");
  const [value, setValue] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [acting, setActing] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const res: any = await axios.get(`${ENDPOINT}?view=summary`, getConfigGet({}));
      if (res?.data?.status) setSummary(res.data.data);
    } catch (error: any) {
      notify(error?.response?.data?.message || "Could not load summary", "error");
    }
  }, [notify]);

  /**
   * The campaign list, as a file marketing can load straight into a push tool.
   *
   * Read only: nobody is put into the lapsed state by this. Stamping the lapse
   * here would start each customer's seven day offer immediately, and these
   * customers are by definition not looking, so most would expire unseen.
   */
  const exportTargets = useCallback(async () => {
    setExporting(true);
    try {
      const res: any = await axios.get(
        `${ENDPOINT}?view=lapse-targets`,
        getConfigGet({})
      );
      const data = res?.data?.data;
      const list: any[] = data?.rows || [];
      if (!list.length) {
        notify("Nobody is quiet enough to target yet", "info");
        return;
      }

      const cols = [
        "userId",
        "email",
        "pushId",
        "registered",
        "shops",
        "lastShopAt",
        "idleDays",
        "ladderValue",
      ];
      // Email is chosen by the customer at registration and the validator
      // accepts a leading "=", so a crafted address lands in the file as a
      // formula. Quoting does not help: Excel strips the quotes and then
      // evaluates it. Prefix the cell instead.
      const cell = (v: any) => {
        let s = v === null || v === undefined ? "" : String(v);
        // Leading whitespace too: some spreadsheets trim the cell before
        // evaluating it, so " =HYPERLINK(...)" is still a formula.
        if (/^\s*[=+\-@]|^[\t\r\n]/.test(s)) s = `'${s}`;
        return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [
        cols.join(","),
        ...list.map((r) => cols.map((c) => cell(r[c])).join(",")),
      ].join("\n");

      const stamp = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(
        new Blob([csv], { type: "text/csv;charset=utf-8;" })
      );
      // Appended before clicking and revoked on the next tick: a detached
      // anchor and a synchronous revoke both work in Chromium and have
      // historically dropped the download elsewhere.
      const a = document.createElement("a");
      a.href = url;
      a.download = `lapse-campaign-targets-${stamp}.csv`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);

      // The query has no offset, so rerunning returns the same rows. Say what
      // is true rather than inviting the operator to fetch a next page that
      // does not exist.
      notify(
        data?.truncated
          ? `Exported the ${list.length} longest-quiet customers (the cap). More exist, narrow the cohort or raise the cap to reach them.`
          : `Exported ${list.length} customers`,
        "info"
      );
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Could not build the export",
        "error"
      );
    } finally {
      setExporting(false);
    }
  }, [notify]);

  const loadAnalysis = useCallback(
    async (days: number) => {
      try {
        const res: any = await axios.get(
          `${ENDPOINT}?view=analysis&days=${days}`,
          getConfigGet({})
        );
        if (res?.data?.status) setAnalysis(res.data.data);
      } catch (error: any) {
        notify(
          error?.response?.data?.message || "Could not load performance",
          "error"
        );
      }
    },
    [notify]
  );

  /**
   * Filters are arguments rather than closed-over state so Clear can reset and
   * fetch in one go: the setters above it have not landed when the request is
   * built, and reading them here fetched the filters it had just cleared.
   */
  const loadListWith = useCallback(
    async (
      term: string,
      state: string,
      from: string,
      to: string,
      toPage = 1
    ) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          view: "list",
          limit: String(PAGE_SIZE),
          offset: String((toPage - 1) * PAGE_SIZE),
          search: term,
        });
        if (state) params.set("state", state);
        if (from) params.set("enrolledFrom", from);
        if (to) params.set("enrolledTo", to);
        const res: any = await axios.get(
          `${ENDPOINT}?${params.toString()}`,
          getConfigGet({})
        );
        if (res?.data?.status) {
          setRows(res.data.data.rows || []);
          setTotal(res.data.data.total || 0);
          setPage(toPage);
        }
      } catch (error: any) {
        notify(error?.response?.data?.message || "Could not load list", "error");
      } finally {
        setLoading(false);
      }
    },
    [notify]
  );

  const loadList = useCallback(
    (term: string, toPage = 1) =>
      loadListWith(term, stateFilter, enrolledFrom, enrolledTo, toPage),
    [loadListWith, stateFilter, enrolledFrom, enrolledTo]
  );

  useEffect(() => {
    loadSummary();
    loadAnalysis(trendDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSummary, loadAnalysis, trendDays]);

  // Deliberately not keyed on the filters: changing one should not fire a
  // request mid-typing. The filter row applies on its own button.
  useEffect(() => {
    loadList("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (userId: number) => {
    try {
      const res: any = await axios.get(
        `${ENDPOINT}?view=user&query=${userId}`,
        getConfigGet({})
      );
      if (res?.data?.status) {
        setDetail(res.data.data);
        setAction("");
        setReason("");
        setValue("");
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Could not load customer", "error");
    }
  };

  const chosen = ACTIONS.find((a) => a.key === action);

  const runAction = async () => {
    if (!detail || !chosen) return;
    setActing(true);
    try {
      const body: any = { action: chosen.key, reason };
      if (chosen.field) body[chosen.field] = value;

      const res: any = await axios.patch(
        `${ENDPOINT}/${detail.userId}`,
        body,
        { headers: getConfigPatch() }
      );

      if (res?.data?.status) {
        notify(res.data.message || "Done", "info");
        setDetail(res.data.data);
        setAction("");
        setReason("");
        setValue("");
        loadSummary();
        loadList(search, page);
      } else {
        notify(res?.data?.message || "Action failed", "error");
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Action failed", "error");
    } finally {
      setActing(false);
      setConfirming(false);
    }
  };

  const stat = (label: string, value: any, colour?: string) => (
    <Grid item xs={6} sm={2}>
      <Box className={classes.statBox} style={{ backgroundColor: "#fff" }}>
        <div className={classes.statValue} style={{ color: colour }}>
          {value}
        </div>
        <div className={classes.statLabel}>{label}</div>
      </Box>
    </Grid>
  );

  return (
    <div className={classes.main}>
      <div className={classes.title}>First Thirty Days</div>
      <div className={classes.subtitle}>
        SP-584. Where every enrolled customer is, and the tools to fix what they
        raise. State is worked out the way the app works it out, not from the
        stamps left by their last visit.
      </div>

      {analysis?.recovery && (
        <div className={classes.verdict}>
          <div className={classes.verdictLabel}>Where this stands</div>
          {verdictFor(analysis.recovery, analysis.spend)}
        </div>
      )}

      {analysis && <HealthAlerts health={analysis.health} />}

      <Paper className={classes.tabBar} elevation={0}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Overview" />
          <Tab
            label={`Customers${
              summary?.states?.enrolled ? ` (${summary.states.enrolled})` : ""
            }`}
          />
        </Tabs>
      </Paper>

      {tab === 0 && summary && (
        <>
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            {stat("Enrolled", summary.states.enrolled)}
            {stat("Picked brands", summary.states.confirmedBrands)}
            {stat("Offer open", summary.states.offerOpen, "#e8a838")}
            {stat("Lapsed now", summary.states.lapsedNow, "#c62828")}
            {stat("Concluded", summary.states.concluded, "#6c757d")}
            {stat("Ladder paid", money(summary.rewards.totalPaid), "#2e7d32")}
          </Grid>

          <Paper className={classes.paper}>
            <Typography style={{ fontWeight: 600, marginBottom: 10 }}>
              Where customers are
            </Typography>
            <div style={{ display: "flex" }}>
              {(summary.buckets || []).map((b: any) => (
                <div key={b.bucket} className={classes.bucket}>
                  <div className={classes.bucketValue}>{b.customers}</div>
                  <div className={classes.bucketLabel}>
                    {Number(b.bucket) === summary.config.targetShops
                      ? `${b.bucket}+ shops`
                      : `${b.bucket} shop${Number(b.bucket) === 1 ? "" : "s"}`}
                  </div>
                </div>
              ))}
            </div>
          </Paper>
        </>
      )}

      {tab === 0 && (
        <PerformanceView
          analysis={analysis}
          trendDays={trendDays}
          onTrendDays={setTrendDays}
          exporting={exporting}
          onExport={exportTargets}
        />
      )}

      {tab === 1 && (
        <>
      <Paper className={classes.paper}>
        <div className={classes.filterRow}>
          <TextField
            variant="outlined"
            size="small"
            label="Email or user id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && loadList(search, 1)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <TextField
            select
            variant="outlined"
            size="small"
            label="State"
            value={stateFilter}
            onChange={(e) => setStateFilter(String(e.target.value))}
            style={{ minWidth: 190 }}
          >
            {STATE_FILTERS.map((s) => (
              <MenuItem key={s.value || "any"} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            variant="outlined"
            size="small"
            label="Joined from"
            value={enrolledFrom}
            onChange={(e) => setEnrolledFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ minWidth: 160 }}
          />
          <TextField
            type="date"
            variant="outlined"
            size="small"
            label="Joined to"
            value={enrolledTo}
            onChange={(e) => setEnrolledTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ minWidth: 160 }}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={<SearchIcon />}
            onClick={() => loadList(search, 1)}
          >
            Apply
          </Button>
          {(search || stateFilter || enrolledFrom || enrolledTo) && (
            <Button
              disabled={loading}
              onClick={() => {
                setSearch("");
                setStateFilter("");
                setEnrolledFrom("");
                setEnrolledTo("");
                // Passed explicitly: the state setters above have not landed
                // yet when this request is built.
                loadListWith("", "", "", "", 1);
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </Paper>

      <Paper className={classes.paper}>
        {loading ? (
          <Box textAlign="center" padding={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
              {total === 0
                ? "No customers"
                : `${(page - 1) * PAGE_SIZE + 1} to ${
                    (page - 1) * PAGE_SIZE + rows.length
                  } of ${total}`}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Shops</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Brands</TableCell>
                  <TableCell>Last shop</TableCell>
                  <TableCell>Offer ends</TableCell>
                  <TableCell>Earned</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.userId}
                    hover
                    className={classes.row}
                    onClick={() => openDetail(r.userId)}
                  >
                    <TableCell>
                      {r.email}
                      <div style={{ fontSize: 11, color: "#999" }}>{r.userId}</div>
                    </TableCell>
                    <TableCell>{r.shops}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.state}
                        style={{
                          backgroundColor: stateColour(r.state),
                          color: "#fff",
                        }}
                      />
                      {r.lapsedUnseen && (
                        <div style={{ fontSize: 10, color: "#c62828" }}>
                          not seen yet
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{r.brandsPicked}</TableCell>
                    <TableCell>{when(r.lastShopAt)}</TableCell>
                    <TableCell>{when(r.lapseEndsAt)}</TableCell>
                    <TableCell>{money(r.earned)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={(e: any) => {
                          e.stopPropagation();
                          openDetail(r.userId);
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} style={{ color: "#888" }}>
                      No customers match that search.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <Box display="flex" alignItems="center" justifyContent="center">
              <IconButton
                disabled={page <= 1 || loading}
                onClick={() => loadList(search, page - 1)}
              >
                <NavigateBeforeIcon />
              </IconButton>
              <Typography variant="body2">
                Page {page} of {Math.max(Math.ceil(total / PAGE_SIZE), 1)}
              </Typography>
              <IconButton
                disabled={page >= Math.ceil(total / PAGE_SIZE) || loading}
                onClick={() => loadList(search, page + 1)}
              >
                <NavigateNextIcon />
              </IconButton>
            </Box>
          </>
        )}
      </Paper>
        </>
      )}

      <Dialog
        open={detail != null}
        onClose={() => setDetail(null)}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle disableTypography>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <div>
              <Typography style={{ fontSize: 18, fontWeight: 700 }}>
                {detail?.email}
              </Typography>
              <Typography style={{ fontSize: 12, color: "#666" }}>
                user {detail?.userId} · {detail?.state} · {detail?.shops} shops
                {detail?.manualShopCredit !== 0 &&
                  ` (${detail?.countedShops} counted, ${detail?.manualShopCredit} by support)`}
              </Typography>
            </div>
            <IconButton
              size="small"
              onClick={() => setDetail(null)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
          <>
            <Grid container spacing={2} style={{ marginBottom: 8 }}>
              {stat("Shops", detail.shops)}
              {stat("Bought", detail.spend?.count ?? 0)}
              {stat("Spent", money(detail.spend?.total), "#0a1735")}
              {stat("Brands picked", detail.brandsPicked ?? 0)}
              {stat("Earned", money(detail.earned), "#2e7d32")}
              {stat("Still to earn", money(detail.stillToEarn))}
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography style={{ fontWeight: 600, fontSize: 13 }}>
                  Picked brands
                </Typography>
                <Table size="small">
                  <TableBody>
                    {(detail.brands || []).map((b: any) => (
                      <TableRow key={b.id}>
                        <TableCell>{b.brand_name_at_pick}</TableCell>
                        <TableCell>{b.brand_id}</TableCell>
                        <TableCell>
                          {b.shopped_at ? "shopped" : "not yet"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography style={{ fontWeight: 600, fontSize: 13 }}>
                  Ladder
                </Typography>
                <Typography style={{ fontSize: 11, color: "#888" }}>
                  The win-back reward. Rungs appear once a customer goes quiet
                  for {summary?.config?.lapseThresholdDays ?? 7} days, priced
                  by the shop count they stalled at.
                </Typography>
                <Table size="small">
                  <TableBody>
                    {rungs.map((r: any) => (
                      <TableRow key={r.shopNumber}>
                        <TableCell>shop {r.shopNumber}</TableCell>
                        <TableCell>{money(r.amount)}</TableCell>
                        <TableCell
                          style={{ color: r.paid ? "#2e7d32" : "#888" }}
                        >
                          {r.state}
                        </TableCell>
                        <TableCell style={{ fontSize: 11, color: "#999" }}>
                          {r.id ?? ""}
                        </TableCell>
                      </TableRow>
                    ))}
                    {rungs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} style={{ color: "#888" }}>
                          {detail.state === "lapsed"
                            ? "Lapsed, but no rungs recorded yet."
                            : "Nothing owed. This customer has not gone quiet."}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>

            {SUPPORT_ACTIONS_ENABLED && (
            <>
            <Typography
              style={{ fontWeight: 600, fontSize: 13, marginTop: 16 }}
            >
              Support action
            </Typography>
            <div className={classes.actionRow}>
              <TextField
                select
                variant="outlined"
                size="small"
                label="Action"
                value={action}
                onChange={(e) => setAction(String(e.target.value))}
                style={{ minWidth: 220 }}
              >
                {ACTIONS.map((a) => (
                  <MenuItem key={a.key} value={a.key}>
                    {a.label}
                    {a.privileged ? " *" : ""}
                  </MenuItem>
                ))}
              </TextField>

              {chosen?.field && (
                <TextField
                  variant="outlined"
                  size="small"
                  label={chosen.field}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  style={{ width: 120 }}
                />
              )}

              <TextField
                variant="outlined"
                size="small"
                label="Reason (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{ minWidth: 320 }}
              />

              <Button
                variant="contained"
                color="primary"
                disabled={!action || reason.trim().length < 10}
                onClick={() => setConfirming(true)}
              >
                Apply
              </Button>
            </div>
            <div className={classes.warn}>
              Actions marked * change what this customer can earn, in either
              direction, and need the admin or ops role. Every action is
              recorded against your account with the reason you give.
            </div>
            </>
            )}

            <Typography
              style={{ fontWeight: 600, fontSize: 13, marginTop: 16 }}
            >
              History
            </Typography>
            <Table size="small">
              <TableBody>
                {(detail.audit || []).map((a: any, i: number) => {
                  const refused = refusalOf(a);
                  return (
                  <TableRow key={a.id ?? i}>
                    <TableCell>{when(a.createdAt)}</TableCell>
                    <TableCell>
                      {a.action}
                      {refused && (
                        <div style={{ fontSize: 10, color: "#c62828" }}>
                          refused: {refused}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{a.admin_email}</TableCell>
                    <TableCell>
                      {a.old_value} to {a.new_value}
                    </TableCell>
                    <TableCell style={{ fontSize: 11, color: "#666" }}>
                      {a.reason}
                    </TableCell>
                  </TableRow>
                  );
                })}
                {(detail.audit || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} style={{ color: "#888" }}>
                      No support action has been taken on this customer.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <Typography style={{ fontWeight: 600, fontSize: 13, marginTop: 16 }}>
              What they bought
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Counted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detail.purchases || []).map((p: any, i: number) => (
                  <TableRow key={`${p.source}-${p.at}-${i}`}>
                    <TableCell>{when(p.at)}</TableCell>
                    <TableCell>{SHOP_SOURCES[p.source] ?? p.source}</TableCell>
                    <TableCell>{p.brand}</TableCell>
                    <TableCell>{money(p.amount)}</TableCell>
                    <TableCell>{p.status}</TableCell>
                    <TableCell
                      style={{ color: Number(p.counts) ? "#2e7d32" : "#999" }}
                    >
                      {Number(p.counts) ? "yes" : "no"}
                    </TableCell>
                  </TableRow>
                ))}
                {(detail.purchases || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} style={{ color: "#888" }}>
                      Nothing bought yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Apply {chosen?.label}?</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 14 }}>
            {detail?.email} (user {detail?.userId})
            {chosen?.field ? ` · ${chosen.field}: ${value}` : ""}
          </Typography>
          <Typography style={{ fontSize: 13, color: "#666", marginTop: 8 }}>
            {reason}
          </Typography>
          {chosen?.privileged && (
            <Typography style={{ fontSize: 13, color: "#c62828", marginTop: 8 }}>
              This changes what this customer can earn and needs the admin or
              ops role. Without it the action is refused.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button color="primary" onClick={runAction} disabled={acting}>
            {acting ? "Applying..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default NewUserBoostDashboard;
